import React from 'react';
import { useIntl } from 'react-intl';
import { Container, Row, Col } from 'reactstrap';
import { TypedMemo } from '../common/types';
import { SMLContainer } from '../smartLinks/smlContainer/SMLContainer';
import { SMLBreadcrumbs } from '../smartLinks/smlBreadcrumbs/SMLBreadcrumbs';
import { SMLRouteNames } from '../../routing/sml/SMLRoutes';
import { useArtistDetails } from './hooks/useArtistDetails';
import { LoadingView } from '../common/views/LoadingView';
import { ErrorView } from '../common/views/ErrorView/ErrorView';
import { TopOverallTracksCard } from './components/TopOverallTracksCard/TopOverallTracksCard';
import { TopGainingTracksCard } from './components/TopGainingTracksCard/TopGainingTracksCard';
import { SMLDatePicker } from './components/SMLDatePicker/SMLDatePicker';
import { SMLCountrySelector } from './components/SMLCountrySelector/SMLCountrySelector';
import { ArtistStatsRow } from './components/ArtistStatsRow/ArtistStatsRow';
import { ArtistPositionChangeChart } from './components/ArtistPositionChangeChart/ArtistPositionChangeChart';
import { ArtistPlaylistScoreChart } from './components/ArtistPlaylistScoreChart/ArtistPlaylistScoreChart';
import { FollowButton } from './components/FollowButton/FollowButton';
import { SharePubliclyButton } from './components/SharePublicly/SharePubliclyButton';
import { SMLImage } from './components/SMLImage';
import { PublicModeError } from './components/PublicModeError/PublicModeError';
import { useShareTokenContext } from './contexts/ShareTokenContext';
import { useEntityIdParam } from './hooks/useEntityIdParam';
import { useCanViewListeningInsights } from './hooks/useCanViewListeningInsights';
import { ArtistListeningScoreChart } from './components/ArtistListeningScoreChart/ArtistListeningScoreChart';
import { ChartsBackLink } from './charts/ChartsBackLink';
import { useChartsDetailCrumbs } from './charts/useChartsDetailCrumbs';
import { useMobilePageTitle } from './navigation/hooks/useMobilePageTitle';

const SMLArtistDetailsComponent: React.FC = () => {
  const intl = useIntl();
  const { payload, isPublicMode } = useShareTokenContext();
  const artistId = useEntityIdParam('artist');

  const {
    data: artistData,
    isLoading,
    error,
    refetch,
  } = useArtistDetails({
    artistId: artistId ?? '',
  });
  // Design b4: the mobile top bar names the entity of this deep page.
  useMobilePageTitle(artistData?.mainName);

  const canViewListeningInsights = useCanViewListeningInsights();

  const crumbName =
    artistData?.mainName ||
    intl.formatMessage({ id: 'app.sml.breadcrumbs.artist-details', defaultMessage: 'Artist Details' });
  const breadcrumbs = useChartsDetailCrumbs(SMLRouteNames.artists, crumbName, isPublicMode);
  // design navigation-v2 e 5c: a page under Charts has a back link; a public share link has none.
  const back = isPublicMode ? null : <ChartsBackLink tab={SMLRouteNames.artists} />;

  if (isPublicMode && payload?.t !== 'artist') {
    return (
      <SMLContainer publicMode header={null}>
        <PublicModeError />
      </SMLContainer>
    );
  }

  if (artistId === null) {
    return (
      <SMLContainer
        publicMode={isPublicMode}
        header={
          <SMLBreadcrumbs breadcrumbs={breadcrumbs} variant="eyebrow" hideCrumbsOnPhone={!isPublicMode}>
            <div className="d-flex align-items-center gap-2">
              {!isPublicMode && <SMLCountrySelector />}
              <SMLDatePicker showRangePresets />
            </div>
          </SMLBreadcrumbs>
        }
      >
        {back}
        <div style={{ padding: '40px', textAlign: 'center' }}>
          <p>{intl.formatMessage({ id: 'app.sml.artist.details.no-id', defaultMessage: 'No artist ID provided' })}</p>
        </div>
      </SMLContainer>
    );
  }

  if (error && isPublicMode) {
    return (
      <SMLContainer publicMode header={null}>
        <PublicModeError />
      </SMLContainer>
    );
  }

  if (error) {
    return (
      <SMLContainer
        publicMode={isPublicMode}
        header={
          <SMLBreadcrumbs breadcrumbs={breadcrumbs} variant="eyebrow" hideCrumbsOnPhone={!isPublicMode}>
            <div className="d-flex align-items-center gap-2">
              {!isPublicMode && <SMLCountrySelector />}
              <SMLDatePicker showRangePresets />
            </div>
          </SMLBreadcrumbs>
        }
      >
        {back}
        <ErrorView
          error={error}
          retry={() => {
            refetch().catch(console.error);
          }}
        />
      </SMLContainer>
    );
  }

  if (isLoading) {
    return (
      <SMLContainer
        publicMode={isPublicMode}
        header={
          <SMLBreadcrumbs breadcrumbs={breadcrumbs} variant="eyebrow" hideCrumbsOnPhone={!isPublicMode}>
            <div className="d-flex align-items-center gap-2">
              {!isPublicMode && <SMLCountrySelector />}
              <SMLDatePicker showRangePresets />
            </div>
          </SMLBreadcrumbs>
        }
      >
        {back}
        <LoadingView />
      </SMLContainer>
    );
  }

  return (
    <SMLContainer
      publicMode={isPublicMode}
      header={
        <SMLBreadcrumbs breadcrumbs={breadcrumbs} variant="eyebrow" hideCrumbsOnPhone={!isPublicMode}>
          <SMLDatePicker showRangePresets />
        </SMLBreadcrumbs>
      }
    >
      {back}
      <Container fluid>
        {/* Artist Name Header */}
        <Row className="mb-4">
          <Col>
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
              <div className="d-flex align-items-center gap-3">
                <SMLImage
                  src={artistData?.imageUrl}
                  alt={artistData?.mainName || 'Artist'}
                  width={64}
                  makeSquareFit
                  className="rounded-circle"
                  style={{
                    width: '64px',
                    height: '64px',
                    objectFit: 'cover',
                    flexShrink: 0,
                  }}
                />
                <h1 className="mb-0">{artistData?.mainName}</h1>
              </div>
              <div className="d-flex align-items-center gap-2">
                {!isPublicMode && (
                  <SharePubliclyButton
                    type="artist"
                    id={artistId}
                    shareTitle={intl.formatMessage(
                      {
                        id: 'app.sml.sharePublicly.title.artist',
                        defaultMessage: '{name} on MUSIC24',
                      },
                      { name: artistData?.mainName ?? '' }
                    )}
                    name={artistData?.mainName}
                    coverUrl={artistData?.imageUrl ?? undefined}
                    subtitle={intl.formatMessage({
                      id: 'app.sml.sharePublicly.type.artist',
                      defaultMessage: 'Artist',
                    })}
                  />
                )}
                <FollowButton artistId={artistId} variant="button" size="lg" />
              </div>
            </div>
          </Col>
        </Row>

        {/* Stats Cards Row */}
        <ArtistStatsRow />

        {/* Charts Row */}
        <Row>
          <Col md={6} className="mt-4">
            <ArtistPositionChangeChart />
          </Col>
          <Col md={6} className="mt-4">
            <ArtistPlaylistScoreChart />
          </Col>
          {canViewListeningInsights && (
            <Col md={6} className="mt-4">
              <ArtistListeningScoreChart />
            </Col>
          )}
        </Row>

        {/* Top Tracks for Artist */}
        <Row>
          <Col md={6} className="mt-4">
            <TopOverallTracksCard artistId={artistId} />
          </Col>
          <Col md={6} className="mt-4">
            <TopGainingTracksCard artistId={artistId} />
          </Col>
        </Row>
      </Container>
    </SMLContainer>
  );
};

export const SMLArtistDetails = TypedMemo(SMLArtistDetailsComponent);
